import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useFieldArray, useForm } from 'react-hook-form';
import { CollegeEnum } from '@fasstech/oip-core/entities/enums';
import { offerCodeEnum } from '@@viewEnums';
import useSaveSimulationDemographyMutation from './useSaveSimulationDemographyMutation.js';
import useGetSimulationDemographyOfferQuery from './useGetSimulationDemographyOfferQuery.js';
import { useEffect, useRef, useState } from 'react';
import { and, anyPass, filter, gt, includes, isEmpty, isNil, path, propOr } from 'ramda';
import {
  buildZoneOptions,
  canRemoveZone,
  emptyDemographyBlock,
  getCoverageZoneFlags,
  getSelectedZones,
  isAddZoneDisabled
} from '@@viewHelpers/coverageZone.js';
import {
  mapDemographiesToFormValues,
  mapFormValuesToOfferCheck,
  mapFormValuesToPayload
} from '../helpers/formValues.js';
import { buildBoundsRules, getBounds } from '../helpers/demographyBounds.js';
import { buildPayrollRule, getPayrollRows } from '../helpers/payroll.js';

const useSimulationDemographyForm = (
  getDemography,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean,
  coverageZones,
  middleFeatures
) => {
  const { processHandlers, context } = useProcess();
  const { onFormSubmit } = useSaveSimulationDemographyMutation();

  const coverageZoneFlags = getCoverageZoneFlags(
    getMiddleBoolean,
    getMiddleText
  );

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: mapDemographiesToFormValues(getDemography)
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'demographies',
    keyName: 'uuid'
  });

  const selectedZones = getSelectedZones(form.watch('demographies'));

  const { refetch: refetchOffer } = useGetSimulationDemographyOfferQuery({
    skip: true
  });
  const [hasNoOffer, setHasNoOffer] = useState(false);
  // Contrôle en échec (erreur réseau ou KO serveur) : on bloque sans afficher le message
  // « pas d'offre », puisqu'on ne sait pas s'il y en a une
  const [offerCheckFailed, setOfferCheckFailed] = useState(false);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const lastOfferCheck = useRef(null);
  const offerCheck = mapFormValuesToOfferCheck(
    {
      college: form.watch('college'),
      demographies: form.watch('demographies')
    },
    { isMultiZone: coverageZoneFlags.isDisplayed }
  );
  const offerCheckKey = JSON.stringify(offerCheck);

  useEffect(() => {
    lastOfferCheck.current = offerCheckKey;

    // Prévoyance collective : le contrôle porte sur le collège, sans zone de couverture
    const isGroupProtection =
      getDemography?.offerCode === offerCodeEnum.GROUP_PROTECTION;

    if (isEmpty(offerCheck.zones) && !(isGroupProtection && offerCheck.college)) {
      setHasNoOffer(false);
      setOfferCheckFailed(false);
      setLoadingOffer(false);
      return;
    }

    setLoadingOffer(true);
    refetchOffer(offerCheck)
      .then(({ data }) => {
        if (lastOfferCheck.current !== offerCheckKey) return;
        const hasOffer = path(
          ['getSimulationDemographyOffer_simulation_demography', 'hasOffer'],
          data
        );
        setHasNoOffer(hasOffer === false);
        setOfferCheckFailed(isNil(hasOffer));
      })
      .catch((err) => {
        console.error(
          'Erreur lors du contrôle d\'offre par zone de couverture :',
          err
        );
        if (lastOfferCheck.current !== offerCheckKey) return;
        setHasNoOffer(false);
        setOfferCheckFailed(true);
      })
      .finally(() => {
        if (lastOfferCheck.current === offerCheckKey) setLoadingOffer(false);
      });
  }, [offerCheckKey]);

  const handleAddZone = () => append(emptyDemographyBlock());

  const handleRemoveZone = (index) => {
    form.clearErrors(`demographies.${index}`);
    remove(index);

    setTimeout(() => {
      form.clearErrors('demographies');
      form.trigger();
    }, 150);
  };

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const { isValid } = form.formState;

  const collegeOptions = [
    {
      label: 'Ensemble du personnel',
      value: CollegeEnum.ALL
    },
    {
      label: 'Cadres',
      value: CollegeEnum.EXECUTIVE_EMPLOYEES
    },
    {
      label: 'Non cadres',
      value: CollegeEnum.EMPLOYEES
    }
  ];

  const filteredCollegeOptions = filter(
    ({ label }) => includes(label, propOr([], 'colleges', getDemography)),
    collegeOptions
  );

  const boundsOf = getBounds(getMiddleNumber, getMiddleText);
  const boundsRules = buildBoundsRules(getMiddleNumber, getMiddleText);
  const payrollRows = getMiddleBoolean(
    'ENTREPRISE_DEMOGRAPHIE_MASSE_SALARIALE_EST_NECESSAIRE'
  )
    ? getPayrollRows(middleFeatures)
    : [];
  const payrollRule = buildPayrollRule(getMiddleText);
  const tooltipText = (field) => {
    const { min, max, info } = boundsOf(field);
    return anyPass([
      () => and(gt(min, -1), gt(max, -1)),
      () => gt(max, -1),
      () => gt(min, -1)
    ])()
      ? info
      : undefined;
  };

  const onSubmit = (values) => {
    onFormSubmit(
      mapFormValuesToPayload(values, {
        isMultiZone: coverageZoneFlags.isDisplayed
      }),
      (ok, err) => {
        if (ok && !err) {
          processHandlers.onNext({
            context: {
              projectId: context?.projectId
            }
          });
        }
      }
    );
  };

  return {
    form,
    fields,
    hasNoOffer,
    offerCheckFailed,
    loadingOffer,
    coverageZoneFlags,
    zoneOptionsAt: (index) =>
      buildZoneOptions(coverageZones, selectedZones, index),
    addZoneIsDisabled: isAddZoneDisabled(coverageZones, selectedZones),
    canRemoveZone,
    handleAddZone,
    handleRemoveZone,
    $onPrevClick,
    isValid,
    collegeOptions: filteredCollegeOptions,
    employeeCountTooltipText: tooltipText('employeeCount'),
    spouseCountTooltipText: tooltipText('partnerCount'),
    childrenCountTooltipText: tooltipText('childrenCount'),
    employeeAverageAgeTooltipText: tooltipText('employeeAverageAge'),
    boundsRules,
    payrollRows,
    payrollRule,
    onSubmit
  };
};

export default useSimulationDemographyForm;
